import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  DirectionsCar as CarIcon,
  Pets as PetsIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ISchedule, ScheduleStatus } from "../types";
import { updateScheduleStatus } from "../services";

interface AgendamentoCardProps {
  schedule: ISchedule;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: () => void;
}

const AgendamentoCard: React.FC<AgendamentoCardProps> = ({
  schedule,
  onEdit,
  onDelete,
  onStatusChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [changingStatus, setChangingStatus] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit();
  };

  const handleDelete = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    setDeleteDialogOpen(false);
    onDelete();
  };

  const handleStatusChange = async (newStatus: ScheduleStatus) => {
    handleMenuClose();
    setChangingStatus(true);
    try {
      await updateScheduleStatus(schedule.id, newStatus);
      onStatusChange();
    } catch (error) {
    } finally {
      setChangingStatus(false);
    }
  };

  const getStatusColor = (
    status: ScheduleStatus
  ): "default" | "primary" | "secondary" | "error" | "success" | "warning" => {
    switch (status) {
      case ScheduleStatus.PENDENTE:
        return "default";
      case ScheduleStatus.CONFIRMADO:
        return "primary";
      case ScheduleStatus.EM_ANDAMENTO:
        return "warning";
      case ScheduleStatus.CONCLUIDO:
        return "success";
      case ScheduleStatus.CANCELADO:
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: ScheduleStatus): string => {
    switch (status) {
      case ScheduleStatus.PENDENTE:
        return "Pendente";
      case ScheduleStatus.CONFIRMADO:
        return "Confirmado";
      case ScheduleStatus.EM_ANDAMENTO:
        return "Em Andamento";
      case ScheduleStatus.CONCLUIDO:
        return "Concluído";
      case ScheduleStatus.CANCELADO:
        return "Cancelado";
      default:
        return status;
    }
  };

  return (
    <Card
      elevation={changingStatus ? 0 : 1}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        opacity: changingStatus ? 0.6 : 1,
        transition: "all 0.3s",
        borderLeft: 3,
        borderColor: `${getStatusColor(schedule.status)}.main`,
        bgcolor: "background.paper",
        "&:hover": {
          elevation: 3,
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent sx={{ flex: 1, py: 1, px: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          {/* Horário */}
          <Chip
            icon={<ScheduleIcon />}
            label={format(new Date(schedule.scheduledAt), "HH:mm", {
              locale: ptBR,
            })}
            size="small"
            variant="outlined"
            sx={{ fontWeight: "bold", minWidth: 80 }}
          />

          {/* Pet e Tutor */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              minWidth: 150,
              gap: 0.5,
            }}
          >
            <PetsIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            <Box>
              <Typography variant="body2" fontWeight="600">
                {schedule.pet?.name || "Pet não encontrado"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {schedule.pet?.tutor?.name || ""}
              </Typography>
            </Box>
          </Box>

          {/* Serviço */}
          <Chip
            label={schedule.serviceName}
            size="small"
            sx={{ fontWeight: 500, minWidth: 100 }}
          />

          {/* Status e Badges */}
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Chip
              label={getStatusLabel(schedule.status)}
              color={getStatusColor(schedule.status)}
              size="small"
              sx={{ fontWeight: "bold" }}
            />
            {schedule.requiresPickup && (
              <Chip
                icon={<CarIcon />}
                label="Buscar"
                size="small"
                variant="outlined"
                color="warning"
              />
            )}
            {schedule.isRecurring && (
              <Chip
                label="↻"
                size="small"
                variant="outlined"
                color="info"
                title={schedule.recurrencePattern || "Recorrente"}
              />
            )}
          </Box>
        </Box>

        {/* Observações - Compactas */}
        {schedule.notes && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mt: 0.5,
              pl: 1,
              borderLeft: 2,
              borderColor: "divider",
              fontStyle: "italic",
            }}
          >
            {schedule.notes}
          </Typography>
        )}
      </CardContent>

      {/* Menu de ações - Compacto */}
      <Box sx={{ pr: 0.5 }}>
        <IconButton onClick={handleMenuOpen} size="small" color="primary">
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handleEdit} dense>
            <Typography variant="body2">Editar</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => handleStatusChange(ScheduleStatus.CONFIRMADO)}
            disabled={schedule.status === ScheduleStatus.CONFIRMADO}
            dense
          >
            <Typography variant="body2">Confirmar</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => handleStatusChange(ScheduleStatus.EM_ANDAMENTO)}
            disabled={schedule.status === ScheduleStatus.EM_ANDAMENTO}
            dense
          >
            <Typography variant="body2">Iniciar</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => handleStatusChange(ScheduleStatus.CONCLUIDO)}
            disabled={schedule.status === ScheduleStatus.CONCLUIDO}
            dense
          >
            <Typography variant="body2">Concluir</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => handleStatusChange(ScheduleStatus.CANCELADO)}
            disabled={schedule.status === ScheduleStatus.CANCELADO}
            dense
          >
            <Typography variant="body2">Cancelar</Typography>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleDelete} dense sx={{ color: "error.main" }}>
            <Typography variant="body2">Excluir</Typography>
          </MenuItem>
        </Menu>
      </Box>

      {/* Dialog de confirmação de exclusão - Compacto */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Confirmar Exclusão</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <DialogContentText component="div">
            <Typography variant="body2" gutterBottom>
              Tem certeza que deseja excluir este agendamento?
            </Typography>
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                bgcolor: "grey.50",
                borderRadius: 1,
                borderLeft: 3,
                borderColor: "error.main",
              }}
            >
              <Typography variant="caption" display="block" gutterBottom>
                <strong>Pet:</strong> {schedule.pet?.name}
              </Typography>
              <Typography variant="caption" display="block" gutterBottom>
                <strong>Serviço:</strong> {schedule.serviceName}
              </Typography>
              <Typography variant="caption" display="block">
                <strong>Data/Hora:</strong>{" "}
                {format(
                  new Date(schedule.scheduledAt),
                  "dd/MM/yyyy 'às' HH:mm",
                  { locale: ptBR }
                )}
              </Typography>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} size="small">
            Cancelar
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            size="small"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default AgendamentoCard;
